import * as React from 'react';

/**
 * useReducedMotion — returns true when the user prefers reduced motion.
 *
 * SSR/undefined-safe: returns false when window is not available (e.g. SSR, test jsdom
 * without matchMedia). Subscribes to the `change` event so the value stays reactive
 * if the user toggles the OS accessibility setting at runtime.
 *
 * Pattern mirrors `use-media-query.ts` in this directory.
 *
 * Usage:
 *   const reduced = useReducedMotion();
 *   <motion.div transition={{ duration: reduced ? 0 : 0.4 }} />
 */
export function useReducedMotion(): boolean {
  const [value, setValue] = React.useState(() => {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    return false;
  });

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setValue(mediaQuery.matches);

    function onChange(event: MediaQueryListEvent) {
      setValue(event.matches);
    }

    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  return value;
}
